// stores/authStore.ts
import type { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'
import { supabase } from '../lib/supabase'

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  signIn(email: string, password: string): Promise<void>
  signUp(email: string, password: string): Promise<void>
  signInWithGoogle(): Promise<void>
  signOut(): Promise<void>
  refreshSession(): Promise<void>
}

export const useAuth = create<AuthState>((set, _get) => {
  // Initialize auth state
  const initializeAuth = async () => {
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession()

      // Validate session exists and is not expired
      if (session && session.expires_at && new Date(session.expires_at * 1000) > new Date()) {
        set({ session, user: session.user, loading: false })
      } else {
        // Session is null or expired - clear it
        set({ session: null, user: null, loading: false })
      }
    } catch (error) {
      set({ session: null, user: null, loading: false })
    }
  }

  // Start initialization
  set({ loading: true })
  initializeAuth()

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, session) => {
    // Handle sign out or invalid session - force logout
    if (event === 'SIGNED_OUT' || !session) {
      set({ session: null, user: null })
    } else {
      set({ session, user: session?.user ?? null })
    }
  })

  return {
    session: null,
    user: null,
    loading: true,

    async signIn(email: string, password: string) {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error
    },

    async signUp(email: string, password: string) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmed`
        }
      })

      if (error) throw error

      // Check if user already exists (identities will be empty for existing users)
      if (data.user?.identities?.length === 0) {
        throw new Error('An account with this email already exists. Please sign in instead.')
      }
    },

    async signInWithGoogle() {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      })

      if (error) throw error
    },

    async signOut() {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },

    async refreshSession() {
      const {
        data: { session },
        error
      } = await supabase.auth.refreshSession()
      if (!error && session) {
        set({ session, user: session.user })
      }
    }
  }
})
